import { useState, useEffect } from "react";
import Buttons from "../../../components/Buttons/Buttons";
import { customInstance } from "../../../api/custom-instance";
import { User } from "../../../api/models";
import { ASSIGNABLE_ROLES } from "./constants";

export default function ManageRolesModal({ isOpen, onClose, user, onSuccess, setPopupMessage, isAdmin }: {
    isOpen: boolean,
    onClose: () => void,
    user: User | null,
    onSuccess: () => void,
    setPopupMessage: (msg: string, success: boolean) => void,
    isAdmin: boolean,
}) {
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (user && user.ac_roles) {
            setSelectedRoles([...(user.ac_roles as string[])]);
        }
    }, [user]);

    const handleSave = async () => {
        if (!user) return;
        setIsSubmitting(true);
        try {
            const currentRoles = new Set((user.ac_roles || []) as string[]);
            const newRoles = new Set(selectedRoles);

            // Roles to add
            const toAdd = selectedRoles.filter(r => !currentRoles.has(r));
            // Roles to remove
            const toRemove = ((user.ac_roles || []) as string[]).filter(r => !newRoles.has(r));

            if (toAdd.length > 0) {
                await customInstance({
                    url: `users/${user.username}/roles`,
                    method: 'PUT',
                    data: { roles: toAdd },
                });
            }
            if (toRemove.length > 0) {
                await customInstance({
                    url: `users/${user.username}/roles`,
                    method: 'DELETE',
                    data: { roles: toRemove },
                });
            }

            setPopupMessage(`Roles updated for '${user.username}'.`, true);
            onSuccess();
            onClose();
        } catch (err: any) {
            const detail = err?.response?.data?.detail || 'Failed to update roles.';
            setPopupMessage(detail, false);
        }
        setIsSubmitting(false);
    };

    if (!isOpen || !user) return null;

    const availableRoles = isAdmin
        ? [...ASSIGNABLE_ROLES, 'admin', 'cpi_employee', 'service_account']
        : ASSIGNABLE_ROLES;

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-lg w-full max-w-[400px] p-6 flex flex-col gap-4">
                <h2 className="text-lg font-semibold text-default">Manage Roles — {user.username}</h2>
                <div className="flex flex-wrap gap-2">
                    {availableRoles.map(role => (
                        <label key={role} className="flex items-center gap-1 text-sm cursor-pointer">
                            <input type="checkbox" checked={selectedRoles.includes(role)} onChange={() => setSelectedRoles((prev: string[]) => prev.includes(role) ? prev.filter((r: string) => r !== role) : [...prev, role])} />
                            {role}
                        </label>
                    ))}
                </div>
                <div className="flex justify-end gap-2 mt-2">
                    <Buttons id="cancel-roles" type="tertiary" styling="outline" label="Cancel" action={onClose} />
                    <Buttons id="save-roles" type={isSubmitting ? 'disabled' : 'primary'} styling="solid" label={isSubmitting ? 'Saving...' : 'Save'} action={handleSave} />
                </div>
            </div>
        </div>
    );
}
